import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Search, CircleXBold, ChevronDown } from "@/components/icons";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type SearchFieldStatus = "empty" | "filled" | "focused";

// Context for SearchField
interface SearchFieldContextValue {
  status: SearchFieldStatus;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  setInputRef: (node: HTMLInputElement | null) => void;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  hasValue: boolean;
  setHasValue: React.Dispatch<React.SetStateAction<boolean>>;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  searchableItems: string[];
  selectedCategory: string;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string>>;
}

const SearchFieldContext = React.createContext<SearchFieldContextValue | null>(
  null,
);

const useSearchFieldContext = () => {
  return React.useContext(SearchFieldContext);
};

// SearchField Container 스타일
const searchFieldVariants = cva(
  "flex items-center justify-between bg-fills-neutral transition-shadow rounded-lg w-[324px] w-full px-3 py-2",
  {
    variants: {
      status: {
        empty: "inset-border-solid-normal",
        filled: "inset-border-solid-normal bg-static-white",
        focused: "inset-border-focus bg-static-white",
      },
      disabled: {
        true: "cursor-not-allowed inset-border-solid-normal",
        false: "",
      },
    },
    defaultVariants: {
      status: "empty",
      disabled: false,
    },
  },
);

// SearchField Component
interface SearchFieldProps extends React.ComponentProps<"div"> {
  disabled?: boolean;
  /** 검색 타입(SearchFieldDropdown) 목록 */
  items?: string[];
}

function SearchField({
  className,
  disabled = false,
  items = [],
  children,
  ...props
}: SearchFieldProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState(
    items[0] ?? "",
  );

  const searchableItems = React.useMemo(() => items, [items]);

  const setInputRef = React.useCallback((node: HTMLInputElement | null) => {
    (inputRef as { current: HTMLInputElement | null }).current = node;
  }, []);

  const status: SearchFieldStatus = React.useMemo(() => {
    if (isFocused) return "focused";
    if (hasValue) return "filled";
    return "empty";
  }, [isFocused, hasValue]);

  const contextValue: SearchFieldContextValue = {
    status,
    disabled,
    inputRef,
    setInputRef,
    isFocused,
    setIsFocused,
    hasValue,
    setHasValue,
    inputValue,
    setInputValue,
    searchableItems,
    selectedCategory,
    setSelectedCategory,
  };

  return (
    <SearchFieldContext.Provider value={contextValue}>
      <div
        data-slot="search-field"
        data-status={status}
        data-disabled={disabled}
        className={cn("relative", className)}
        {...props}
      >
        <div
          data-slot="search-field-container"
          className={cn(searchFieldVariants({ status, disabled }))}
        >
          {children}
        </div>
      </div>
    </SearchFieldContext.Provider>
  );
}

// SearchFieldDropdown Component
interface SearchFieldDropdownProps {
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

function SearchFieldDropdown({
  className,
  value,
  onValueChange,
  disabled: propDisabled,
}: SearchFieldDropdownProps) {
  const context = useSearchFieldContext();
  const disabled = context?.disabled ?? propDisabled ?? false;
  const searchableItems = context?.searchableItems ?? [];
  const selectedCategory =
    context?.selectedCategory ?? searchableItems[0] ?? "";
  const setSelectedCategory = context?.setSelectedCategory;

  const displayValue = value !== undefined ? value : selectedCategory;

  const handleValueChange = (item: string) => {
    // 드롭다운 표시값 업데이트
    setSelectedCategory?.(item);
    onValueChange?.(item);
  };

  return (
    <Select
      value={displayValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger
        data-slot="search-field-dropdown"
        className={cn(
          "flex items-center justify-between shrink-0 w-20 mr-2 pr-2! h-auto! p-0 border-0 border-r border-lines-strong rounded-none shadow-none",
          "typo-label-2-medium text-labels-primary",
          "focus:ring-0 focus-visible:ring-0 focus-visible:border-lines-strong",
          "*:data-[slot=select-icon]:hidden", // Hide default chevron icon
          disabled && "cursor-not-allowed text-labels-alternative",
          className,
        )}
      >
        <SelectValue className="flex-1">
          <span className="flex-1 text-left truncate">{displayValue}</span>
        </SelectValue>
        <ChevronDown
          size={16}
          color="labels-alternative"
          className="shrink-0"
        />
      </SelectTrigger>
      <SelectContent sideOffset={8}>
        <SelectGroup>
          {searchableItems.map((item) => (
            <SelectItem key={item} value={item}>
              {item}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

// SearchFieldInput Component
interface SearchFieldInputProps extends Omit<
  React.ComponentProps<"input">,
  "size"
> {
  onSearch?: (value: string) => void;
}

const SearchFieldInput = React.forwardRef<
  HTMLInputElement,
  SearchFieldInputProps
>(
  (
    {
      className,
      value: controlledValue,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      placeholder,
      onSearch,
      ...props
    },
    ref,
  ) => {
    const context = useSearchFieldContext();
    const isInContext = !!context;

    const status = context?.status ?? "empty";
    const disabled = context?.disabled ?? props.disabled ?? false;
    const setInputRef = context?.setInputRef;
    const setIsFocused = context?.setIsFocused;
    const setHasValue = context?.setHasValue;
    const inputValue = context?.inputValue ?? "";
    const setInputValue = context?.setInputValue;

    // controlled value와 internal value 동기화
    const displayValue =
      controlledValue !== undefined ? controlledValue : inputValue;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused?.(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused?.(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setHasValue?.(!!newValue);
      setInputValue?.(newValue);
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        onSearch?.(String(displayValue));
      }

      onKeyDown?.(e);
    };

    // value prop이 변경될 때 hasValue 업데이트
    React.useEffect(() => {
      if (isInContext) {
        setHasValue?.(!!displayValue);
      }
    }, [displayValue, setHasValue, isInContext]);

    // 텍스트 색상 결정
    const getTextColor = () => {
      if (disabled) return "text-labels-alternative";
      if (status === "filled" || status === "focused")
        return "text-labels-primary";
      return "text-labels-assistive";
    };

    return (
      <div className="flex flex-1 items-center gap-2">
        <Search size={20} color="labels-alternative" />
        <input
          ref={(node) => {
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
            setInputRef?.(node);
          }}
          type="text"
          data-slot="search-field-input"
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? "검색어를 입력하세요..."}
          autoComplete="off"
          className={cn(
            "flex-1 bg-transparent outline-none min-w-0",
            "typo-label-1-medium",
            "placeholder:text-labels-assistive",
            getTextColor(),
            disabled && "cursor-not-allowed",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
SearchFieldInput.displayName = "SearchFieldInput";

// SearchFieldClearButton Component
type SearchFieldClearButtonProps = React.ComponentProps<"button">;

function SearchFieldClearButton({
  className,
  ...props
}: SearchFieldClearButtonProps) {
  const context = useSearchFieldContext();

  if (!context) {
    console.warn("SearchFieldClearButton must be used within SearchField");
    return null;
  }

  const { status, disabled, inputRef, setHasValue, setInputValue } = context;

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!inputRef.current) return;

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value",
    )?.set;
    nativeInputValueSetter?.call(inputRef.current, "");

    const event = new Event("input", { bubbles: true });
    inputRef.current.dispatchEvent(event);

    setHasValue(false);
    setInputValue("");
    inputRef.current.focus();
  };

  // empty 상태에서는 투명하게 표시 (공간은 유지)
  const isVisible = status !== "empty";

  return (
    <button
      type="button"
      data-slot="search-field-clear-button"
      disabled={disabled || !isVisible}
      onMouseDown={handleClear}
      className={cn(
        "shrink-0 flex items-center",
        isVisible
          ? "cursor-pointer opacity-100"
          : "opacity-0 pointer-events-none",
        disabled && "cursor-not-allowed",
        className,
      )}
      tabIndex={-1}
      {...props}
    >
      <CircleXBold size={20} color="fills-strong" />
    </button>
  );
}

// displayName 설정
SearchField.displayName = "SearchField";
SearchFieldDropdown.displayName = "SearchFieldDropdown";
SearchFieldClearButton.displayName = "SearchFieldClearButton";

export {
  SearchField,
  SearchFieldDropdown,
  SearchFieldInput,
  SearchFieldClearButton,
  searchFieldVariants,
};
